import { useState, useMemo } from 'react';
import { Loader2, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react';
import { useDeals, useCreateDeal } from '@/lib/hooks';
import {
  TransactionStructure,
  transactionStructureLabels,
  transactionStructureIcons,
  stageLabels,
  stageColors,
  getDealPriorityColor,
  getDaysUntilClose,
  getTimelineColor,
  type TransactionStructureType,
} from '@/types/database';
import { formatCurrency, formatPercentage, cn } from '@/lib/utils';

type FilterType = 'all' | TransactionStructureType;
type SortField = 'name' | 'target_value' | 'closing_probability' | 'estimated_close_date' | 'true_discount';
type SortDirection = 'asc' | 'desc';

// Priority color classes for row backgrounds
const priorityRowColors = {
  green: 'bg-positive-subtle hover:bg-positive-subtle/80 border-l-4 border-l-positive',
  yellow: 'bg-warning-subtle hover:bg-warning-subtle/80 border-l-4 border-l-warning',
  red: 'bg-negative-subtle hover:bg-negative-subtle/80 border-l-4 border-l-negative',
};

// Priority badge colors
const priorityBadgeColors = {
  green: 'bg-positive text-white border-positive',
  yellow: 'bg-warning text-white border-warning',
  red: 'bg-negative text-white border-negative',
};

export function DealsPage(): React.ReactElement {
  const [structureFilter, setStructureFilter] = useState<FilterType>('all');
  const [showNewDealModal, setShowNewDealModal] = useState(false);
  const [sortField, setSortField] = useState<SortField>('closing_probability');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const { data, isLoading, error } = useDeals(
    structureFilter === 'all' ? undefined : { structure: structureFilter }
  );
  const createDeal = useCreateDeal();

  const deals = data?.data ?? [];

  // Sort deals based on current sort state
  const sortedDeals = useMemo(() => {
    return [...deals].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'target_value':
          comparison = a.target_value - b.target_value;
          break;
        case 'closing_probability':
          const probA = a.closing_probability ?? 50;
          const probB = b.closing_probability ?? 50;
          comparison = probA - probB;
          break;
        case 'estimated_close_date':
          const daysA = getDaysUntilClose(a) ?? 999;
          const daysB = getDaysUntilClose(b) ?? 999;
          comparison = daysA - daysB;
          break;
        case 'true_discount':
          const discA = a.true_discount ?? 0;
          const discB = b.true_discount ?? 0;
          comparison = discA - discB;
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [deals, sortField, sortDirection]);

  const structureCounts = {
    all: deals.length,
    whole_lp_interest: deals.filter(d => d.transaction_structure === 'whole_lp_interest').length,
    partial_strip: deals.filter(d => d.transaction_structure === 'partial_strip').length,
    multi_fund_portfolio: deals.filter(d => d.transaction_structure === 'multi_fund_portfolio').length,
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 text-text-tertiary" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="h-4 w-4 text-primary" />
      : <ArrowDown className="h-4 w-4 text-primary" />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-negative font-medium">Failed to load deals</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Deals</h2>
          <p className="text-text-secondary">All deals in your portfolio - click headers to sort</p>
        </div>
        <button
          onClick={() => setShowNewDealModal(true)}
          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover transition-colors shadow-sm font-medium"
        >
          + New Deal
        </button>
      </div>

      {/* Color Legend */}
      <div className="flex items-center gap-6 text-sm bg-surface p-4 rounded-lg border border-border shadow-sm">
        <span className="text-text-secondary font-semibold">Priority:</span>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-positive shadow-sm" />
          <span className="text-text-primary">High (≥70% prob, $1M+)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-warning shadow-sm" />
          <span className="text-text-primary">Medium</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-negative shadow-sm" />
          <span className="text-text-primary">Concerns / Low Prob</span>
        </div>
      </div>

      {/* Transaction Structure Filter Tabs */}
      <div className="flex gap-2 border-b border-border pb-0">
        <button
          onClick={() => setStructureFilter('all')}
          className={cn(
            'px-4 py-2 rounded-t-md text-sm font-semibold transition-colors border-b-2',
            structureFilter === 'all'
              ? 'bg-primary-subtle text-primary border-primary'
              : 'text-text-secondary hover:bg-surface-hover border-transparent'
          )}
        >
          All Deals ({structureCounts.all})
        </button>
        {Object.entries(TransactionStructure).map(([key, value]) => (
          <button
            key={key}
            onClick={() => setStructureFilter(value)}
            className={cn(
              'px-4 py-2 rounded-t-md text-sm font-semibold transition-colors flex items-center gap-2 border-b-2',
              structureFilter === value
                ? 'bg-primary-subtle text-primary border-primary'
                : 'text-text-secondary hover:bg-surface-hover border-transparent'
            )}
          >
            <span>{transactionStructureIcons[value]}</span>
            {transactionStructureLabels[value]} ({structureCounts[value]})
          </button>
        ))}
      </div>

      {/* Deals Table */}
      <div className="bg-surface rounded-lg border border-border overflow-hidden shadow-sm">
        {deals.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-text-tertiary mb-4 font-medium">No deals yet</p>
            <p className="text-sm text-text-tertiary">
              Create a new deal or run the Deal Sourcing workflow to find opportunities
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('name')}
                    className="flex items-center gap-1 hover:text-text-primary transition-colors"
                  >
                    Deal / Fund
                    <SortIcon field="name" />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  Stage
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('target_value')}
                    className="flex items-center gap-1 hover:text-text-primary ml-auto transition-colors"
                  >
                    Value
                    <SortIcon field="target_value" />
                  </button>
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('closing_probability')}
                    className="flex items-center gap-1 hover:text-text-primary mx-auto transition-colors"
                  >
                    Prob %
                    <SortIcon field="closing_probability" />
                  </button>
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('estimated_close_date')}
                    className="flex items-center gap-1 hover:text-text-primary mx-auto transition-colors"
                  >
                    Timeline
                    <SortIcon field="estimated_close_date" />
                  </button>
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('true_discount')}
                    className="flex items-center gap-1 hover:text-text-primary ml-auto transition-colors"
                  >
                    TRUE %
                    <SortIcon field="true_discount" />
                  </button>
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  Concerns
                </th>
              </tr>
            </thead>
            <tbody className="bg-surface divide-y divide-border">
              {sortedDeals.map((deal) => {
                const priorityColor = getDealPriorityColor(deal);
                const daysUntilClose = getDaysUntilClose(deal);
                const timelineColor = getTimelineColor(daysUntilClose);
                const concerns = deal.concern_areas?.split(',').filter(Boolean) ?? [];

                return (
                  <tr
                    key={deal.id}
                    className={cn(
                      'cursor-pointer transition-all',
                      priorityRowColors[priorityColor]
                    )}
                    onClick={() => window.location.href = `/deals/${deal.id}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-text-primary">{deal.name}</div>
                      <div className="text-sm text-text-tertiary">{deal.seller_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 text-sm text-text-primary">
                        {transactionStructureIcons[deal.transaction_structure as TransactionStructureType]}
                        {deal.transaction_structure === 'partial_strip' && deal.partial_percentage && (
                          <span className="text-text-tertiary font-semibold">
                            ({(deal.partial_percentage * 100).toFixed(0)}%)
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={cn(
                        'inline-flex px-2 py-1 text-xs font-semibold rounded-full border',
                        stageColors[deal.stage as keyof typeof stageColors]
                      )}>
                        {stageLabels[deal.stage as keyof typeof stageLabels]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-text-primary">
                      {formatCurrency(deal.target_value)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={cn(
                        'inline-flex px-2 py-1 text-xs font-bold rounded-full border',
                        priorityBadgeColors[priorityColor]
                      )}>
                        {deal.closing_probability ?? 50}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {daysUntilClose !== null ? (
                        <span className={cn(
                          'inline-flex px-2 py-1 text-xs font-semibold rounded-full border font-mono',
                          timelineColor === 'green' ? 'bg-positive-subtle text-positive border-positive' :
                          timelineColor === 'yellow' ? 'bg-warning-subtle text-warning border-warning' :
                          'bg-negative-subtle text-negative border-negative'
                        )}>
                          {daysUntilClose}d
                        </span>
                      ) : (
                        <span className="text-text-tertiary text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-accent">
                      {deal.true_discount ? formatPercentage(deal.true_discount) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {concerns.length > 0 ? (
                        <div className="flex items-center justify-center gap-1">
                          <AlertTriangle className="h-4 w-4 text-negative" />
                          <span className="text-xs text-negative font-bold">{concerns.length}</span>
                        </div>
                      ) : (
                        <span className="text-positive text-sm">✓</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* New Deal Modal */}
      {showNewDealModal && (
        <NewDealModal
          onClose={() => setShowNewDealModal(false)}
          onCreate={async (data) => {
            await createDeal.mutateAsync(data);
            setShowNewDealModal(false);
          }}
          isLoading={createDeal.isPending}
        />
      )}
    </div>
  );
}

interface NewDealModalProps {
  onClose: () => void;
  onCreate: (data: {
    name: string;
    seller_name: string;
    target_value: number;
    transaction_structure: TransactionStructureType;
    partial_percentage?: number;
    closing_probability?: number;
    estimated_close_date?: string;
  }) => Promise<void>;
  isLoading: boolean;
}

function NewDealModal({ onClose, onCreate, isLoading }: NewDealModalProps): React.ReactElement {
  const [formData, setFormData] = useState({
    name: '',
    seller_name: '',
    target_value: '',
    transaction_structure: 'whole_lp_interest' as TransactionStructureType,
    partial_percentage: '35',
    closing_probability: '50',
    estimated_close_date: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const data: {
      name: string;
      seller_name: string;
      target_value: number;
      transaction_structure: TransactionStructureType;
      partial_percentage?: number;
      closing_probability?: number;
      estimated_close_date?: string;
    } = {
      name: formData.name,
      seller_name: formData.seller_name,
      target_value: parseFloat(formData.target_value),
      transaction_structure: formData.transaction_structure,
      closing_probability: parseInt(formData.closing_probability) || 50,
    };
    if (formData.transaction_structure === 'partial_strip') {
      data.partial_percentage = parseFloat(formData.partial_percentage) / 100;
    }
    if (formData.estimated_close_date) {
      data.estimated_close_date = formData.estimated_close_date;
    }
    await onCreate(data);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal">
      <div className="bg-surface rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl border border-border">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Create New Deal</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Deal Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., Acme Ventures II - LP Position"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Seller Name</label>
            <input
              type="text"
              required
              value={formData.seller_name}
              onChange={(e) => setFormData({ ...formData, seller_name: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., State Pension Fund"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Target Value ($)</label>
            <input
              type="number"
              required
              min="0"
              step="1000"
              value={formData.target_value}
              onChange={(e) => setFormData({ ...formData, target_value: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., 2500000"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Closing Probability (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.closing_probability}
                onChange={(e) => setFormData({ ...formData, closing_probability: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Est. Close Date</label>
              <input
                type="date"
                value={formData.estimated_close_date}
                onChange={(e) => setFormData({ ...formData, estimated_close_date: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Transaction Structure
            </label>
            <div className="grid grid-cols-1 gap-2">
              {Object.entries(TransactionStructure).map(([key, value]) => (
                <label
                  key={key}
                  className={cn(
                    'flex items-center p-3 border rounded-md cursor-pointer transition-colors',
                    formData.transaction_structure === value
                      ? 'border-primary bg-primary-subtle'
                      : 'border-border hover:bg-surface-hover'
                  )}
                >
                  <input
                    type="radio"
                    name="transaction_structure"
                    value={value}
                    checked={formData.transaction_structure === value}
                    onChange={(e) => setFormData({ ...formData, transaction_structure: e.target.value as TransactionStructureType })}
                    className="mr-3"
                  />
                  <span className="mr-2">{transactionStructureIcons[value]}</span>
                  <span className="font-semibold text-text-primary">
                    {transactionStructureLabels[value]}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {formData.transaction_structure === 'partial_strip' && (
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Strip Percentage (%)
              </label>
              <input
                type="number"
                min="25"
                max="50"
                value={formData.partial_percentage}
                onChange={(e) => setFormData({ ...formData, partial_percentage: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="text-xs text-text-tertiary mt-1">Typically 25-50% of the position</p>
            </div>
          )}

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border text-text-secondary rounded-md hover:bg-surface-hover transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover disabled:opacity-50 flex items-center gap-2 shadow-sm font-medium transition-colors"
              disabled={isLoading}
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Deal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
